import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import SparkLogo from './SparkLogo';

/**
 * AdminGuard — protects /admin/* routes.
 *
 * Rules:
 *  1. If no Supabase session -> /admin/login
 *  2. If session but profile.is_admin !== true -> /admin/login with denied flag
 *  3. Uses the server-side helper public.is_admin() as a second check
 *     so a stale local profile cache cannot grant access.
 */
const AdminGuard = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [checked, setChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!user) {
        if (mounted) { setIsAdmin(false); setChecked(true); }
        return;
      }
      try {
        // Ask the DB directly — this cannot be spoofed from the client.
        const { data, error } = await supabase.rpc('is_admin');
        if (error) {
          // eslint-disable-next-line no-console
          console.warn('[AdminGuard] is_admin rpc failed:', error.message);
        }
        if (mounted) {
          setIsAdmin(Boolean(data));
          setChecked(true);
        }
      } catch (e) {
        if (mounted) { setIsAdmin(false); setChecked(true); }
      }
    };
    run();
    return () => { mounted = false; };
  }, [user]);

  if (loading || !checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050816]" data-testid="admin-guard-loading">
        <div className="flex flex-col items-center gap-4">
          <SparkLogo height={48} />
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <div className="w-2 h-2 rounded-full bg-[#ffa726] animate-pulse" />
            Verificando permissões administrativas...
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }
  if (!isAdmin) {
    return <Navigate to="/admin/login" replace state={{ denied: true }} />;
  }

  return children;
};

export default AdminGuard;
